import { OptionCardGroup } from '@rocco/components/questionnaire';
import type { BuildHubOption } from '~/ui-rocco/hooks/use-build-hubs';

import type { MatchAnswers } from '../answers';
import { createInitialAnswers } from '../answers';
import { answersFromProject } from '../project-prefill';

/** Chosen when the brief is not about any project they already have. */
export const SOMETHING_ELSE = 'something-else';

export interface ProjectStepProps {
    answers: MatchAnswers;
    update: (patch: Partial<MatchAnswers>) => void;
    next: () => void;
    projects: BuildHubOption[];
}

const describeProject = (project: BuildHubOption): string | undefined => {
    const place = project.LocationData?.region || project.Location;
    const scope = project.BuildStage;

    return [place, scope].filter(Boolean).join(' · ') || undefined;
};

/**
 * Which project the brief is about, asked before anything else.
 *
 * Only reaches a reader who already has a project. A brief is a need within a
 * project, and naming it up front lets the project answer the questions it
 * already knows instead of asking them again.
 *
 * With one project this is a recognition question — "is this for X?" — rather
 * than a filing one. With several it is a list, most recently active first:
 * there is nothing to match on yet, because nothing has been answered.
 */
export const ProjectStep = ({
    answers,
    update,
    next,
    projects,
}: ProjectStepProps) => {
    const options = [
        ...projects.map((project) => ({
            description: describeProject(project),
            title: project.Title,
            value: project.ID,
        })),
        {
            description:
                projects.length === 1
                    ? 'Start a brief that is not about that project'
                    : 'Start a brief that is not about any of these',
            title: 'Something else',
            value: SOMETHING_ELSE,
        },
    ];

    const selected =
        answers.projectChoice === 'existing' && answers.buildHubID
            ? String(answers.buildHubID)
            : answers.projectChoice === 'new'
              ? SOMETHING_ELSE
              : undefined;

    const handleChange = (value: string) => {
        if (value === SOMETHING_ELSE) {
            // Clearing back to a blank brief rather than leaving a half-filled
            // one behind: they may have picked a project first and changed
            // their mind, and its details must not survive that.
            const blank = createInitialAnswers(answers.enquiryType);
            update({
                budget: blank.budget,
                buildHubID: null,
                projectChoice: 'new',
                location: blank.location,
                projectScope: blank.projectScope,
                projectType: blank.projectType,
                region: blank.region,
                startTiming: blank.startTiming,
                useProjectDetails: false,
            });
            next();
            return;
        }

        const project = projects.find((entry) => entry.ID === value);
        if (!project) return;

        update({ ...answersFromProject(project), projectChoice: 'existing' });
        next();
    };

    return (
        <OptionCardGroup
            dividerBefore={SOMETHING_ELSE}
            dividerLabel="or"
            label="Which project is this for"
            onChange={handleChange}
            options={options}
            value={selected}
        />
    );
};
